'use client';

import { useRef, type KeyboardEvent, type ReactNode } from 'react';

import { Icon, type IconName } from '@eliadmin/core/src/components/dlz/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type SegmentedOption<T extends string> = {
  value: T;
  label: ReactNode;
  icon?: IconName;
};

type SegmentedProps<T extends string> = {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible name of the group. */
  label: string;
  /**
   * dark  – calculator switch on bg/inverse: track bg/inverse-hover, 46 px options (fits content)
   * light – registration / checkout: track bg/surface, 48 px options sharing the width
   */
  tone?: 'dark' | 'light';
  /** Option icon size in px (Figma: 24, the calculator switch 20). */
  iconSize?: 20 | 24;
  className?: string;
};

/**
 * Figma "Tool switch" / "Segmented control": pill track with 4 px padding, the selected option is a
 * white pill (on the light track with the Card hover shadow); DLZ/Body/M Medium, icon 24 (or 20) px.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  tone = 'light',
  iconSize = 24,
  className,
}: SegmentedProps<T>) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  // ARIA radio group: one tab stop, arrow keys / Home / End move and select.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = options.findIndex(option => option.value === value);
    const last = options.length - 1;
    const targets: Partial<Record<string, number>> = {
      ArrowRight: current + 1,
      ArrowDown: current + 1,
      ArrowLeft: current - 1,
      ArrowUp: current - 1,
      Home: 0,
      End: last,
    };
    const next = targets[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const index = next > last ? 0 : next < 0 ? last : next;
    onChange(options[index].value);
    buttons.current[index]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cn(
        'flex rounded-full p-1',
        tone === 'dark' ? 'bg-[#26292E]' : 'gap-1 bg-[#F5F6F7]',
        className,
      )}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={element => {
              buttons.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            className={cn(
              'dlz-body-m-medium inline-flex items-center gap-2 rounded-full px-6 transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D7191F]',
              tone === 'dark' ? 'h-[46px]' : 'h-12 flex-1 justify-center',
              selected
                ? cn('bg-white text-[#0F1012]', tone === 'light' && 'shadow-card-hover')
                : tone === 'dark'
                  ? 'text-white hover:bg-[#1A1C20]'
                  : 'text-[#6B7079] hover:text-[#0F1012]',
            )}
          >
            {option.icon ? (
              <Icon
                name={option.icon}
                size={iconSize}
                className={cn(selected ? 'text-[#D7191F]' : 'text-current')}
              />
            ) : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
